import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';
import type { ThroughputData, ErrorData } from '../types/layers';

/** Throughput data for both directions of a connection */
export interface EdgeThroughput {
  inbound: ThroughputData;
  outbound: ThroughputData;
  errors: ErrorData;
}

/** Response from the throughput metrics API */
export interface TopologyThroughputResponse {
  connections: Record<string, EdgeThroughput>;
}

const backendApi = config.backendApi;

/**
 * What the topology throughput endpoint accepts.
 *
 * Both spellings, because the graph and the panels under it share one range
 * vocabulary (`1h`/`6h`/`24h`/`7d`) while the older `1d`/`1w` forms remain
 * valid. `now` is the live state: the most recent bucket, no range reducer.
 */
export type ThroughputTimeRange = 'now' | '5m' | '1h' | '6h' | '1d' | '24h' | '1w' | '7d';

/** Aggregation method options */
export type ThroughputAggregation = 'max' | 'avg';

interface FetchThroughputParams {
  regionId?: string;
  divisionId?: string;
  timeRange?: ThroughputTimeRange;
  aggregation?: ThroughputAggregation;
  /**
   * Fetch the error and discard counters as well.
   *
   * Off by default on the endpoint, which triples the metric entries per
   * interface. The traffic lens needs it: colour on that canvas means "losing
   * frames", so without this the attention state has no input at all and every
   * link renders healthy by construction.
   */
  includeErrors?: boolean;
}

/** Fetch throughput metrics for topology connections */
const fetchThroughputMetrics = async (params: FetchThroughputParams): Promise<TopologyThroughputResponse> => {
  const queryParams = new URLSearchParams();

  if (params.regionId) {
    queryParams.append('regionId', params.regionId);
  }

  if (params.divisionId) {
    queryParams.append('divisionId', params.divisionId);
  }

  if (params.timeRange) {
    queryParams.append('timeRange', params.timeRange);
  }

  if (params.aggregation) {
    queryParams.append('aggregation', params.aggregation);
  }

  if (params.includeErrors === true) {
    queryParams.append('includeErrors', 'true');
  }

  const queryString = queryParams.toString();
  const url = `${backendApi}/api/metrics/topology/throughput${queryString ? `?${queryString}` : ''}`;

  const response = await axios.get<TopologyThroughputResponse>(url, { withCredentials: true });
  return response.data;
};

/**
 * Hook to fetch throughput metrics for topology connections.
 * Returns throughput data keyed by connection ID.
 */
export function useThroughputMetrics(params: FetchThroughputParams & {
  organizationId?: string | null;
  enabled?: boolean;
  timeRange?: ThroughputTimeRange;
  aggregation?: ThroughputAggregation;
}) {
  const { organizationId, enabled = true, timeRange, aggregation, includeErrors, ...fetchParams } = params;
  const fetchParamsWithOptions = { ...fetchParams, timeRange, aggregation, includeErrors };
  const contextReady = useActiveContextReady();

  const queryEnabled = !!organizationId && contextReady && enabled;

  return useQuery<TopologyThroughputResponse>({
    queryKey: ['throughputMetrics', organizationId, fetchParamsWithOptions],
    queryFn: () => {
      if (!organizationId) {
        throw new Error("organizationId is required to fetch throughput metrics.");
      }
      return fetchThroughputMetrics(fetchParamsWithOptions);
    },
    // Enable the query only if organizationId is available and explicitly enabled
    enabled: queryEnabled,
    // Refresh every 30 seconds for near-real-time updates
    refetchInterval: 30000,
    // Refetch when window regains focus (important after being away from page)
    refetchOnWindowFocus: true,
  });
}
